import { resolveAsanRequest } from './resolveEndpoint';
import { AsanAction, AsanSigningState } from './types';
import useRedirectWithFormId from 'app/utils/customHooks/useRedirectWithFormId';
import { useAsanImzaService } from 'app/utils/services/asanImzaService';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const POLLING_INTERVAL_MS = 5000;

interface Params {
  pathUrl?: any;
  businessKey?: any;
  asanModalType?: string;
  isRegistration?: boolean;
  /** Called with every response at the start of the effect (page-level stage handling). */
  onResponse?: (data?: any) => void;
  /** Called after each response has been processed (at the end of the effect). CSI hooks into this. */
  onResponseHandled?: (data?: any) => void;
  repeatAction?: string;
  taskReference?: string;
}

/**
 * ASAN signing flow: polling, cancel, repeat and the resulting state.
 * Knows nothing about CSI; consumers react through onResponse / onResponseHandled.
 */
const useAsanSigning = ({
  pathUrl,
  businessKey,
  asanModalType,
  isRegistration,
  onResponse,
  onResponseHandled,
  repeatAction,
  taskReference,
}: Params) => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const lang = location.pathname.substring(0, 3);
  const locationState = location.state;

  const { dataAsanImza, asanImzaMutation } = useAsanImzaService();
  const { handleRedirect } = useRedirectWithFormId();
  const [state, setState] = useState<AsanSigningState>({ status: 'verifying' });

  const buildRequest = (action: AsanAction) =>
    resolveAsanRequest({
      action,
      pathUrl,
      businessKey,
      stateBusinessKey: locationState?.business_key,
      repeatAction,
      taskReference,
    });

  const handleCheckNumber = () => {
    if (dataAsanImza?.asan_result === 'user_cancel') return;
    if (pathUrl !== undefined) {
      asanImzaMutation(buildRequest('check'), {
        onError: () => {
          clearTimeout(ref.current);
        },
      });
    } else {
      asanImzaMutation(buildRequest('check'));
    }
  };

  const handleCancel = () => {
    asanImzaMutation(buildRequest('cancel'));
    clearTimeout(ref.current);
  };

  const handleRepeatCode = () => {
    asanImzaMutation(buildRequest('repeat'));
  };

  const callingAsanImzaInterval = () => {
    ref.current = setTimeout(() => {
      handleCheckNumber();
    }, POLLING_INTERVAL_MS);
  };

  // Order of side effects is intentionally identical to the legacy useConfirmAsanImza.
  useEffect(() => {
    if (onResponse) onResponse(dataAsanImza);

    if (dataAsanImza?.is_guest) {
      sessionStorage.setItem('refreshToken', dataAsanImza?.refresh_token);
      sessionStorage.setItem('is_guest', 'true');
      navigate(`${lang}/home-page`);
    }
    if (asanModalType === 'transfer') {
      if (!dataAsanImza?.error && dataAsanImza?.form_id !== 'success_action') {
        callingAsanImzaInterval();
      }
    } else {
      if (!dataAsanImza?.error) {
        callingAsanImzaInterval();
      }
    }

    if (dataAsanImza?.form_id !== 'asan_wait') {
      handleRedirect(dataAsanImza);
    }

    if (dataAsanImza?.form_id === 'success_action') {
      setState({ status: 'success', response: dataAsanImza });
    }

    if (dataAsanImza?.form_id === 'error') {
      // Legacy: success took precedence over error once shown.
      setState(prev =>
        prev.status === 'success' ? prev : { status: 'error', response: dataAsanImza }
      );
    }

    if (dataAsanImza?.result === 'success' && isRegistration) {
      navigate(`${lang}/home-page`);
    }

    if (onResponseHandled) onResponseHandled(dataAsanImza);

    return () => {
      if (ref.current) {
        clearTimeout(ref.current);
      }
    };
  }, [dataAsanImza]);

  const resetState = () => setState({ status: 'verifying' });

  return {
    state,
    resetState,
    dataAsanImza,
    locationState,
    handleCancel,
    handleRepeatCode,
  };
};

export default useAsanSigning;
